
import { Injectable } from '@angular/core';
import { AppService } from './app.service';
import { Organization } from '../models/organization.model';
import { IStripeProduct, IStripeCoupon, IUserOrganization, Service, UserHabit, IProductLink, OrganizationUser, IOrganizationWhiteLabelSettings, OrganizationWhiteLabelAssetKey } from '../models';
import { HelperService } from './helper.service';
import { HttpClient } from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import { DatabaseService } from './database.service';
import { ListenEvent, equalTo, orderByChild } from '@angular/fire/database';
import { StorageService } from './storage.service';
import { BrandingService } from './branding.service';
import { FunctionsService } from './functions.service';
import { ITokenRequest } from './billing.service';
import { safeFindIndex } from 'src/app/utils';
import { INote } from './notes.service';
import { LocalAttachment } from './camera.service';

export interface MessageFolder {
  id: string;
  title: string,
  creationDate: string,
  createdBy: string
}

export interface OrganiationTrainer {
  uid: string;
  firstName: string;
  lastName: string;
}

export interface OrgReferralPayment {
  amount: number;
  invoiceId: string;
  sourceTransactionId: string;
  subscriptionId: string;
  timestamp: string;
}

export interface IWhiteLabelSetupCouponQuote {
  code: string;
  name?: string | null;
  percentOff?: number | null;
  amountOffUsd?: number | null;
  setupFeeUsd: number;
  discountUsd: number;
  totalUsd: number;
}

interface OrgReferralsItem {
  name: string;
  percentage: number;
  status: string;
  amount: number;
  payments: {
    [transactionId: string]: OrgReferralPayment;
  }
}

export interface OrgReferralsObject {
  organizations: {
    [orgId: string]: OrgReferralsItem
  }
}

export interface OrgReferral extends OrgReferralsItem {
  id: string;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {

  private activeOrganizations: Organization[];
  private organization: Organization;
  public organizationSubscription: Subscription;
  public organizationSubject = new Subject<Organization>();

  constructor(
    private appService: AppService,
    private dbService: DatabaseService,
    private brandingService: BrandingService,
    private helperService: HelperService,
    private http: HttpClient,
    private storageService: StorageService,
    private functionsService: FunctionsService,
  ) {
   }

  getAppOrganization(): Organization {
    return this.organization;
  }

  setAppOrganization(orgObj: Organization): void {
    this.organization = orgObj;
    this.initializeOrganization(orgObj.uid);
  }

  clearAppOrganization() {
    this.organization = null;
    if(this.organizationSubscription) {
      this.organizationSubscription.unsubscribe();
    }
    this.publishOrgSubject();
  }

  removeAppOrganization(orgId: string) {
    if(!this.activeOrganizations) {
      return
    }
    const index = safeFindIndex(this.activeOrganizations, org => org.uid === orgId);
    if(index > -1) {
      this.activeOrganizations.splice(index, 1)
    }
  }

  initializeOrganization(orgId) {
    this.clearOrganizationSubscription();
    this.organizationSubscription = this.dbService.getObjectByRef(`organizations/${orgId}`, "uid").subscribe(snap => {
      if(snap) {
        this.organization = new Organization(snap);

        // Set branding
        if(this.organization.hasFeatureAccess("branding") || ["pro", "platinum"].includes(this.organization.plan)) {
          this.brandingService.appBranding.appLogo = this.organization.appLogo;
          this.brandingService.appBranding.logo = this.organization.logo;
          this.brandingService.appBranding.primaryColor = this.organization.color;
          this.brandingService.appBranding.themeFont = this.organization.themeFont;
        }
        this.storageService.set("organization", this.organization.uid);
        
        this.publishOrgSubject();
      }
    });
  }

  clearOrganizationSubscription() {
    if(this.organizationSubscription) {
      this.organizationSubscription.unsubscribe();
    }
  }

  private publishOrgSubject() {
    this.organizationSubject.next(this.organization);
  }

  getActiveOrganizations(): Organization[] {
    return this.activeOrganizations
  }

  setActiveOrganizations(activeOrgs: Organization[]): void {
    this.activeOrganizations = activeOrgs;
  }

  addActiveOrganization(org: Organization) {
    this.activeOrganizations = this.activeOrganizations || [];
    this.activeOrganizations.push(org);
  }

  getPublicOrganization(orgId: string) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgId}/public`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  getPublicSessions(orgId: string) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgId}/public/sessions`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  getPublicSession(orgId: string, sessionId: string) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgId}/public/sessions/${sessionId}`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  getPublicPackage(orgId: string, packageId: string) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgId}/public/packages/${packageId}`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  getPublicTrainers(orgId: string) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgId}/public/trainers`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  async getAllOrganizationsAsPromise() {
    const snap = await this.dbService.getSnapshot(`organizations`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map(org => { return new Organization(org) }) : null;
  }

  async getReferrakOrganizationsAspromise() {
    const snap = await this.dbService.getSnapshot(`organizations`, orderByChild("hasReferral"), equalTo(true));
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map(org => { return new Organization(org) }) : null;
  }

  async getWhiteLabelOrganizationsAsPromise() {
    try {
      const snap = await this.dbService.getSnapshot(
        `organizations`,
        orderByChild('whiteLabelEnabled'),
        equalTo(true),
      );
      return snap
        ? this.helperService.transformFirebaseObjectToArray(snap).map((org) => new Organization(org))
        : [];
    } catch (_err) {
      // Missing .indexOn ["whiteLabelEnabled"] falls back to full scan + client filter.
      const all = await this.getAllOrganizationsAsPromise();
      return (all || []).filter((org) => org.isWhiteLabelEnabled());
    }
  }

  createOrganization
  (
    data: {
      admin: {email: string, uid: string}, 
      discount?: string, 
      name?: string, 
      referral?: string,
      source?: string;
      sourceOther?: string;
      legal: {
        trainerAgreement: {
          accepted: boolean;
          version: string;
        }
      }
    },
  ): Promise<{status: string, id: string}> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/organizations`, data)
      .toPromise()
      .then(res => { resolve(<any>res) })
      .catch(err => { reject(err) })
    });
  }

  updateOrganization(orgId: string, vals: Partial<Organization>) {
    return this.dbService.updateRef(`organizations/${orgId}`, vals);
  }

  updateOrganizationSettings(orgId: string, settingName: string, vals: any) {
    return this.dbService.updateRef(`organizations/${orgId}/settings/${settingName}`, vals);
  }

  updateOrganizationBilling(orgId: string, vals: any) {
    return this.dbService.updateRef(`organizationBilling/${orgId}`, vals);
  }

  async getOrganizationBillingAsPromise(orgId: string): Promise<any> {
    return await this.dbService.getSnapshot(`organizationBilling/${orgId}`);
  }

  async getAllOrganizationBillingAsPromise(): Promise<Record<string, any> | null> {
    return await this.dbService.getSnapshot(`organizationBilling`);
  }

  // This method should use a post and the update should use a put but they were already swapped
  addOrganizationTrial(orgId: string, date: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.put(`${this.appService.getApiURL()}/organizations/${orgId}/trial`, {
        date: date
      })
      .toPromise()
      .then(res => { resolve(<any>res) })
      .catch(err => { reject(err) })
    });
  }

  updateOrganizationTrial(orgId: string, date: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/organizations/${orgId}/trial`, {
        date: date
      })
      .toPromise()
      .then(res => { resolve(<any>res) })
      .catch(err => { reject(err) })
    });
  }

  async getOrganizationAsPromise(orgId: string): Promise<Organization> {
    const snap = await this.dbService.getSnapshot(`organizations/${orgId}`);
    return snap? new Organization({
      uid: orgId,
      ...snap
    }) : null;
  }

  getOrganizationProperty(orgId: string, propName: string) {
    return this.dbService.getSnapshot(`organizations/${orgId}/${propName}`)
  }

  deleteOrganization(orgId: string) {
    return this.http.delete(`${this.appService.getApiURL()}/organizations/${orgId}`)
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  recoverOrganization(orgId: string) {
    return this.http.post(`${this.appService.getApiURL()}/organizations/${orgId}/recover`, {})
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  async getServices(orgId: string): Promise<Service[]> {
    const snap = await this.dbService.getSnapshot(`services/${orgId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map(service => { return new Service(service) }) : null;
  }

  removeOrganizationPriority(orgId: string, clientId: string) {
    return this.dbService.removeRef(`organizationUsers/${orgId}/${clientId}/priority`);
  }

  updateOrganizationStatus(orgName: string, clientId: string, status: IUserOrganization["status"]) {
    return this.dbService.setRef(`users/${clientId}/organizations/${orgName}/status`, status);
  }

  getUser(orgName: string, userId: string) {
    return this.dbService.getObjectByRef(`organizationUsers/${orgName}/${userId}`, 'uid');
  }

  getUserAsPromise(orgName: string, userId: string) {
    return this.dbService.getSnapshot(`organizationUsers/${orgName}/${userId}`);
  }

  updateUser(orgName: string, userId: string, details: {}) {
    return this.dbService.updateRef(`organizationUsers/${orgName}/${userId}`, details)
  }

  getOrganizationUserAsPromise(orgName: string, userId: string) {
    return this.dbService.getSnapshot(`organizationUsers/${orgName}/${userId}`);
  }


  getOrganizationUsers(orgName: string) {
    return this.dbService.getListByRefTest(`organizationUsers/${orgName}`);
  }

  getOrganizationUsersStateChanges(orgName: string) {
    return this.dbService.getStateChangesByRef(`organizationUsers/${orgName}`, [ListenEvent.changed, ListenEvent.removed]);
  }

  getOrganizationUsersStateAdded(orgName: string) {
    return this.dbService.getStateChangesByRef(`organizationUsers/${orgName}`, [ListenEvent.added, ListenEvent.changed, ListenEvent.removed]);
  }

  async getOrganizationUsersAsPromise(orgName: string): Promise<OrganizationUser[]> {
    const val = await this.dbService.getSnapshot(`organizationUsers/${orgName}`);
    return val? this.helperService.transformFirebaseObjectToArray(val) : val;
  }

  updateOrganizationUser(orgId: string, clientId: string, data: any) {
    return this.dbService.updateRef(`organizationUsers/${orgId}/${clientId}`, data)
  }

  async getOrganizationUserByStripeId(orgId: string, stripeId: string) {
    const snap = await this.dbService.getSnapshot(`organizationUsers/${orgId}`, orderByChild("stripeAccount"), equalTo(stripeId));
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : snap;
  }

  async getOrganizationTrainersAsPromise(orgName: string): Promise<OrganiationTrainer[]> {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgName}/trainers`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res: OrganiationTrainer[]) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  async getWorkoutCategories(organizationId: string): Promise<{ name: any, value: string }[]> {
    const categoriesObj = await this.dbService.getSnapshot(`organizations/${organizationId}/settings/programs/workoutPrograms/categories`);
    const categories = [];
    if (categoriesObj) {
      for (const key of Object.keys(categoriesObj)) {
        categories.push({ name: categoriesObj[key], value: key });
      }
    }
    return categories;
  }

  async uploadLogo(organizationId: string, imageData: LocalAttachment): Promise<string> {

    const storagePath = `organizations/${organizationId}/logo`;
    let attachmentRef: string;
    
    if(imageData.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, imageData.file);
    }

    if(imageData.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, imageData.nativePath);
    }

    if(!attachmentRef) {
      throw new Error("Failed to upload image");
    }

    await this.dbService.updateRef('/organizations/' + organizationId, { logo: attachmentRef });
    return attachmentRef;
  }

  async uploadWelcomeMedia(organizationId: string, media: LocalAttachment): Promise<string> {
    const storagePath = `organizations/${organizationId}/welcomeMedia`;
    
    if(media.file) {
      return this.storageService.fbPutFile(storagePath, media.file);
    }

    if(media.nativePath) {
      return this.storageService.fpPutNativeFile(storagePath, media.nativePath);
    }
  }

  async uploadAppLogo(organizationId: string, imageData: LocalAttachment): Promise<string> {
    const storagePath = `organizations/${organizationId}/appLogo`;
    let attachmentRef: string;
    
    if(imageData.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, imageData.file);
    }

    if(imageData.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, imageData.nativePath);
    }

    if(!attachmentRef) {
      throw new Error("Failed to upload image");
    }

    await this.dbService.updateRef('/organizations/' + organizationId, { appLogo: attachmentRef });
    return attachmentRef;
  }

  setWhiteLabelEnabled(orgId: string, enabled: boolean) {
    return this.updateOrganization(orgId, { whiteLabelEnabled: enabled });
  }

  setOrganizationFeature(orgId: string, featureName: string, enabled: boolean) {
    return this.dbService.setRef(`organizations/${orgId}/features/${featureName}`, enabled);
  }

  purchaseWhiteLabelSetup(orgId: string, coupon?: string): Promise<{ status: string }> {
    const body = coupon ? { coupon } : {};
    return this.http.post<{ status: string }>(`${this.appService.getApiURL()}/organizations/${orgId}/white-label/setup`, body)
      .toPromise()
      .then(this.helperService.handlePromiseSuccess)
      .catch(this.helperService.handlePromiseRejection);
  }

  previewWhiteLabelSetupCoupon(orgId: string, coupon: string): Promise<IWhiteLabelSetupCouponQuote> {
    return this.http.post<IWhiteLabelSetupCouponQuote>(
      `${this.appService.getApiURL()}/organizations/${orgId}/white-label/setup/coupon`,
      { coupon },
    )
      .toPromise()
      .then(this.helperService.handlePromiseSuccess)
      .catch(this.helperService.handlePromiseRejection);
  }

  updateWhiteLabelWorkflowStatus(orgId: string, workflowStatus: IOrganizationWhiteLabelSettings['workflowStatus']) {
    return this.dbService.updateRef(`organizations/${orgId}/settings/whiteLabel`, { workflowStatus: workflowStatus });
  }

  saveWhiteLabelSettings(orgId: string, vals: IOrganizationWhiteLabelSettings) {
    return this.updateOrganizationSettings(orgId, 'whiteLabel', vals);
  }

  async uploadWhiteLabelAsset(
    organizationId: string,
    assetKey: OrganizationWhiteLabelAssetKey,
    imageData: LocalAttachment
  ): Promise<string> {
    const fileName = imageData.name || `${assetKey}-${Date.now()}.png`;
    const storagePath = `organizations/${organizationId}/whiteLabel/${assetKey}/${fileName}`;
    let attachmentRef: string;

    if (imageData.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, imageData.file);
    }

    if (imageData.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, imageData.nativePath);
    }

    if (!attachmentRef) {
      throw new Error('Failed to upload white-label asset');
    }

    return attachmentRef;
  }

  updateEquipmentList(orgName: string, EquipmentList: boolean) {
    return this.dbService.updateRef(`organizations/${orgName}/settings/clients`, { defaultEquipmentList: EquipmentList });
  }
  
  updateFoodPreferences(orgName: string, FoodPreferences: boolean) {
    return this.dbService.updateRef(`organizations/${orgName}/settings/clients`, { defaultFoodPreferences: FoodPreferences });
  }

  updateSettings(orgName: string, settingName: string, data: any) {
    return this.dbService.updateRef(`organizations/${orgName}/settings/${settingName}`, data);
  }

  async addClientTypes(orgName: string, optionArray: any) {
    return this.dbService.updateRef(`organizations/${orgName}/settings/clients/profileFields/clientType`, { options: optionArray });
  }

  async addClientPriorities(orgName: string, priorties) {
    return this.dbService.updateRef(`organizations/${orgName}/settings/clients/profileFields/clientPriority`, { options: priorties.options || null, emptyTitle: priorties.emptyTitle || null });
  }

  // Org Notes
  async getOrganizationNotesAsPRomise(orgId: string): Promise<INote[]> {
    const snap = await this.dbService.getSnapshot(`organizationNotes/${orgId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
  }

  getOrganizationNotesList(orgId: string) {
    return this.dbService.getListByRefTest(`organizationNotes/${orgId}`);
  }

  deleteOrganizationNote(orgId: string, noteId :string) {
    return this.dbService.removeRef(`organizationNotes/${orgId}/${noteId}`);
  }

  async setDefaultNotes(orgName: string, type: string, notes) {
    return this.dbService.setRef(`organizations/${orgName}/settings/programs/${type}/defaultNotes`, notes);
  }

  async updateWPExerciseTypes(orgName: string, optionArray: any) {
    return this.dbService.setRef(`organizations/${orgName}/settings/programs/workoutPrograms/exerciseTypes`, optionArray);
  }

  async addWPExerciseTypeVideo(orgId: string, etId: string, attachment: LocalAttachment): Promise<string> {

    const storagePath = `organizations/${orgId}/exerciseTypes/${etId}/${attachment.name}`;

    if(attachment.file) {
      return this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      return this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }
  }

  async addWPCategories(orgName: string, optionArray: any) {
    return this.dbService.setRef(`organizations/${orgName}/settings/programs/workoutPrograms/categories`, optionArray);
  }

  async getServicesOrderbyTitle(orgId: string): Promise<Service[]> {
    const snap = await this.dbService.getSnapshot(`services/${orgId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map(service => { return new Service(service) }) : null;
  }
  
  async addServices(orgName: string, Item: any) {
    return this.dbService.pushByRef(`services/${orgName}`, {...Item });
  }

  removeServices(orgName: string, Id: string) {
    return this.dbService.removeRef(`services/${orgName}/${Id}`);
  }

  archiveServices(orgName: string, Id: string) {
    return this.dbService.updateRef(`services/${orgName}/${Id}`, { status: "archived" });
  }

  unArchiveServices(orgName: string, Id: string) {
    return this.dbService.updateRef(`services/${orgName}/${Id}`, { status: "active" });
  }

  async getServicesbyId(orgId: string, Id: string): Promise<Service[]> {
    const snap = await this.dbService.getSnapshot(`services/${orgId}/${Id}`)
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map(service => { return new Service(service) }) : null;
  }

  async editServices(orgName: string, Id: string, Item: any) {
    return this.dbService.updateRef(`services/${orgName}/${Id}`, {...Item})
  }

  addOrganizationAdmin(organizationId: string, trainerId: string) {
    return this.dbService.setRef(`organizations/${organizationId}/auth/admin/members/${trainerId}`, true);
  }

  removeOrganizationAdmin(organizationId: string, trainerId: string) {
    return this.dbService.removeRef(`organizations/${organizationId}/auth/admin/members/${trainerId}`);
  }

  removeOrganizationUser(organizationId: string, userId: string) {
    return this.dbService.removeRef(`organizationUsers/${organizationId}/${userId}`);
  }

  getBillingInfo(orgId: string) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgId}/billing`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }
  
  addPaymentMethod(orgId: string, data: ITokenRequest){
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/organizations/${orgId}/billing`, data)
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  removePaymentMethod(orgId: string, methodId: string) {
    return this.http.delete(`${this.appService.getApiURL()}/organizations/${orgId}/billing/methods/${methodId}`)
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  getActiveSubscriptions(orgId: string) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgId}/subscriptions`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  getSubscription(orgId: string, subscriptionId) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgId}/subscriptions/${subscriptionId}`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  cancelSubscription(orgId: string, details?: {
    reason?: string;
    details?: string;
    mode?: "immediate" | "period_end";
    immediate?: boolean;
    source?: "self_serve" | "admin";
    cancel?: boolean;
  }) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/organizations/${orgId}/cancel`, {cancel: true, ...details})
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  undoCancelSubscription(orgId: string) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/organizations/${orgId}/cancel/undo`, {})
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  applyRetentionDiscount(orgId: string, details: {
    arm: "fifty_off_two_months" | "fifty_off_one_month" | "free_month";
    reason?: string;
    details?: string;
  }) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/organizations/${orgId}/retention/discount`, details)
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  recordRetentionEvent(orgId: string, details: {
    event: string;
    reason?: string;
    arm?: string;
    step?: string;
  }) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/organizations/${orgId}/retention/event`, details)
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  getAccountPayments(orgId: string) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgId}/payments`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  getAccountInvoices(orgId: string) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgId}/invoices`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  payAccountInvoice(orgId: string, invoiceId: string) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/organizations/${orgId}/invoices/${invoiceId}`;
      this.http.put(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  refundAccountInvoice(orgId: string, invoiceId?: string, amount?: number) {
    return new Promise((resolve, reject) => {
      this.http.put(`${this.appService.getApiURL()}/organizations/${orgId}/invoices/${invoiceId}/refund`, {status: "refunded", invoiceId: invoiceId, amount: amount})
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  async getDefaultSupplementProtocol(orgId: string) {
    const snap = await this.dbService.getSnapshot(`organizations/${orgId}/settings/programs/supplementProtocols/defaultSupplements`);
    return snap? snap : [];
  }

  saveDefaultSupplementProtocol(orgId: string, categories) {
    return this.dbService.setRef(`organizations/${orgId}/settings/programs/supplementProtocols/defaultSupplements`, categories);
  }

  upgradePlan(orgId: string, subscription: any) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/organizations/${orgId}/upgrade`, subscription)
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  getStripeAccount(orgId): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/connect/${orgId}`)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  getStripeAccountBalance(orgId, accountKey?: string): Promise<string> {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/balance`;
    if (accountKey) {
      url += `?accountKey=${encodeURIComponent(accountKey)}`;
    }
    return new Promise((resolve, reject) => {
      this.http.get(url)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  getStripeAccountPayouts(orgId, accountKey?: string): Promise<any> {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/payouts`;
    if (accountKey) {
      url += `?accountKey=${encodeURIComponent(accountKey)}`;
    }
    return new Promise((resolve, reject) => {
      this.http.get(url)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  getStripeAccountReport(orgId, start: number, end: number, accountKey?: string): Promise<any> {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/reports?start=${start}&end=${end}`;
    if (accountKey) {
      url += `&accountKey=${encodeURIComponent(accountKey)}`;
    }
    return new Promise((resolve, reject) => {
      this.http.get(url)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  getStripeOnboardingUrl(orgId): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/connect/${orgId}/onboarding`)
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationStripeAccounts(orgId: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/connect/${orgId}/accounts`)
      .toPromise()
      .then(res => { resolve(<any[]>res || []) })
      .catch(err => { reject(err) })
    });
  }

  createOrganizationStripeAccount(orgId: string, label: string, trainerId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/connect/${orgId}/accounts`, { label, trainerId })
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationStripeAccountOnboardingUrl(orgId: string, accountKey: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/connect/${orgId}/accounts/${accountKey}/onboarding`)
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  deleteOrganizationStripeAccount(orgId: string, accountKey: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.delete(`${this.appService.getApiURL()}/connect/${orgId}/accounts/${accountKey}`)
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationInvoices(orgId): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/connect/${orgId}/invoices`)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationPayment(orgId: string, paymentId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/connect/${orgId}/payments/${paymentId}`)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationUpcomingPayments(orgId, limit = 20, startAfter?: string, userId?: string, accountKey?: string): Promise<any> {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/upcoming?limit=${limit}`;
    if(startAfter) {
      url += `&startAfter=${startAfter}`
    }
    if(userId) {
      url += `&customer=${userId}`
    }
    if(accountKey) {
      url += `&accountKey=${encodeURIComponent(accountKey)}`
    }
    return new Promise((resolve, reject) => {
      this.http.get(url)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationPayments(orgId, limit = 20, startAfter?: string, userId?: string, accountKey?: string): Promise<any> {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/payments?limit=${limit}`;
    if(startAfter) {
      url += `&startAfter=${startAfter}`
    }
    if(userId) {
      url += `&customer=${userId}`
    }
    if(accountKey) {
      url += `&accountKey=${encodeURIComponent(accountKey)}`
    }
    return new Promise((resolve, reject) => {
      this.http.get(url)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationFailedPayments(orgId, limit = 20, page?: number, accountKey?: string): Promise<any> {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/failedPayments?limit=${limit}`;
    if(page) {
      url += `&page=${page}`
    }
    if(accountKey) {
      url += `&accountKey=${encodeURIComponent(accountKey)}`
    }
    return new Promise((resolve, reject) => {
      this.http.get(url)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationSubscriptions(orgId): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/connect/${orgId}/subscriptions`)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationPackages(orgId, accountKey?: string): Promise<any> {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/products`;
    if (accountKey) {
      url += `?accountKey=${encodeURIComponent(accountKey)}`;
    }
    return new Promise((resolve, reject) => {
      this.http.get(url)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationPackage(orgId, packageId): Promise<IStripeProduct> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/connect/${orgId}/products/${packageId}`)
      .toPromise()
      .then(res => { resolve(<IStripeProduct>res) })
      .catch(err => { reject(err) })
    });
  }

  createOrganizationPackage(orgId, data: Partial<IStripeProduct>): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/connect/${orgId}/products`, data)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  updateOrganizationPackage(orgId, packageId: string, data: Partial<IStripeProduct>): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http.put(`${this.appService.getApiURL()}/connect/${orgId}/products/${packageId}`, data)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  archiveOrganizationPackage(orgId, packageId: string, isArchived: boolean): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.put(`${this.appService.getApiURL()}/connect/${orgId}/products/${packageId}/archive`, {archived: isArchived})
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  deleteOrganizationPackage(orgId, packageId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.delete(`${this.appService.getApiURL()}/connect/${orgId}/products/${packageId}`)
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationPackagePrices(orgId, packageId): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/connect/${orgId}/products/${packageId}/prices`)
      .toPromise()
      .then(res => { resolve(<IStripeProduct>res) })
      .catch(err => { reject(err) })
    });
  }

  async uploadOrganizationPackageImage(organizationId: string, attachment: LocalAttachment): Promise<any> {
    if(attachment.file) {
      return this.storageService.fbPutFile(`organizations/${organizationId}/packages/${attachment.name}`, attachment.file);
    }

    if(attachment.nativePath) {
      return this.storageService.fpPutNativeFile(`organizations/${organizationId}/packages/${attachment.name}`, attachment.nativePath);
    }

    throw new Error("No file or native path provided");
  }

  /* Coupons */
  getOrganizationCoupon(orgId: string, couponId: string, accountKey?: string): Promise<IStripeCoupon> {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/coupons/${couponId}`;
    if (accountKey) {
      url += `?accountKey=${encodeURIComponent(accountKey)}`;
    }
    return new Promise((resolve, reject) => {
      this.http.get(url)
      .toPromise()
      .then(res => { resolve(<IStripeCoupon>res) })
      .catch(err => { reject(err) })
    });
  }

  createOrganizationCoupon(orgId: string, data: Partial<IStripeCoupon>, accountKey?: string) {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/coupons`;
    if (accountKey) {
      url += `?accountKey=${encodeURIComponent(accountKey)}`;
    }
    return new Promise((resolve, reject) => {
      this.http.post(url, data)
      .toPromise()
      .then(res => { resolve(<IStripeCoupon>res) })
      .catch(err => { reject(err) })
    });
  }

  updateOrganizationCoupon(orgId: string, couponId: string, data: Partial<IStripeCoupon>, accountKey?: string) {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/coupons/${couponId}`;
    if (accountKey) {
      url += `?accountKey=${encodeURIComponent(accountKey)}`;
    }
    return new Promise((resolve, reject) => {
      this.http.put(url, data)
      .toPromise()
      .then(res => { resolve(<IStripeCoupon>res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationCoupons(orgId, accountKey?: string): Promise<any> {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/coupons`;
    if (accountKey) {
      url += `?accountKey=${encodeURIComponent(accountKey)}`;
    }
    return new Promise((resolve, reject) => {
      this.http.get(url)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  deleteOrganizationCoupon(orgId: string, couponId: string, accountKey?: string): Promise<any> {
    let url = `${this.appService.getApiURL()}/connect/${orgId}/coupons/${couponId}`;
    if (accountKey) {
      url += `?accountKey=${encodeURIComponent(accountKey)}`;
    }
    return new Promise((resolve, reject) => {
      this.http.delete(url)
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  createOrganizationQuote(orgId: string, data: any) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/connect/${orgId}/quotes`, data)
      .toPromise()
      .then(res => { resolve(<IStripeCoupon>res) })
      .catch(err => { reject(err) })
    });
  }

  getOrganizationTaxQuote(orgId: string, data: any) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/connect/${orgId}/tax`, data)
      .toPromise()
      .then(res => { resolve(<IStripeCoupon>res) })
      .catch(err => { reject(err) })
    });
  }

  async addClientAdmin(orgId: string, userId) {
    const promises = [];
    promises.push(this.dbService.setRef(`users/${userId}/organizations/${orgId}`, {type: "client", status: "active"}));
    promises.push(this.dbService.setRef(`/organizations/${orgId}/clients/${userId}`, true));
    // Overwrite to ensure the is removed flag is cleated
    promises.push(this.dbService.removeRef(`/organizationUsers/${orgId}/${userId}/isRemoved`))
    return Promise.all(promises)
  }

  addTrainer(orgName: string, userObj: any): Promise<Object> {
    let trainerUrl = `${this.appService.getApiURL()}/organizations/${orgName}/trainers`;
    return this.http.post(trainerUrl, userObj).toPromise()
    .then(this.handleData)
    .catch(this.handleRejection)
  }

  removeTrainer(orgName: string, trainerId: string, client = false): Promise<Object> {
    let trainerUrl = `${this.appService.getApiURL()}/organizations/${orgName}/trainers/${trainerId}`;
    return this.http.delete(trainerUrl, {body: {isClient: client}}).toPromise()
    .then(this.handleData)
    .catch(this.handleRejection)
  }

  acceptBillingTerms(orgId: string, userId: string, terms: string) {
    return this.dbService.setRef(`organizations/${orgId}/billingTerms`, {
      acceptedOn: new Date().toISOString(),
      acceptedBy: userId,
      terms: terms,
    });
  }

  getSavedMessagesList(orgId: string) {
    return this.dbService.getListByRefTest(`organizations/${orgId}/settings/chat/savedMessages`);
  }

 async getSavedMessages(orgId: string) {
  const snap = await this.dbService.getSnapshot(`organizations/${orgId}/settings/chat/savedMessages`);
  return snap? this.helperService.transformFirebaseObjectToArray(snap).map(msg => {
    if(!msg.hasOwnProperty("order")) {
      msg.order = 1000
    }
    return msg
  })
  .sort(function(a, b){ 
    return a.order - b.order 
  }) : null;
 }

  async addSavedMessage(orgId: string, data: {title?: string, message: string, shared?: boolean, attachments?: any, folders?: string[], createdBy: string}) {
    return this.dbService.pushByRef(`organizations/${orgId}/settings/chat/savedMessages`, {
      title: data.title || null,
      message: data.message || null,
      shared: data.shared || false,
      attachments: data.attachments || null,
      folders: data.folders || null,
      createdBy: data.createdBy || null,
      creationDate: new Date().toISOString(),
    });
  }

  async updateSavedMessage(orgId: string, messageId: string,  data: {title?: string, message?: string, order?: number, attachments?: any[], shared?: boolean, folders?: string[]}) {
    return this.dbService.updateRef(`organizations/${orgId}/settings/chat/savedMessages/${messageId}`, {
      ...data,
      lastModifiedDate: new Date().toISOString(),
    });
  }

  deleteSavedMessage(orgId: string, messageId: string) {
    return this.dbService.removeRef(`organizations/${orgId}/settings/chat/savedMessages/${messageId}`);
  }

  // Folders
  getSavedFoldersList(orgId: string) {
    return this.dbService.getListByRefTest(`organizations/${orgId}/settings/chat/folders`);
  }

  async getSavedFoldersAsPromise(orgId: string): Promise<MessageFolder[]> {
    const snap = await this.dbService.getSnapshot(`organizations/${orgId}/settings/chat/folders`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : null
  }

  addMessageFolder(orgId: string, data: Partial<MessageFolder>) {
    return this.dbService.pushByRef(`organizations/${orgId}/settings/chat/folders`, data);
  }

  updateMessageFolder(orgId: string, folderId: string, data: Partial<MessageFolder>) {
    return this.dbService.updateRef(`organizations/${orgId}/settings/chat/folders/${folderId}`, data);
  }

  deleteMessageFolder(orgId: string, folderId: string) {
    return this.dbService.removeRef(`organizations/${orgId}/settings/chat/folders/${folderId}`);
  }

  // Habits
  async getHabitsAsPromise(orgId: string) {
    const snap = await this.dbService.getSnapshot(`organizations/${orgId}/settings/clients/habits`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
  }

  getHabitAsPromise(orgId: string, habitId: string) {
    return this.dbService.getSnapshot(`organizations/${orgId}/settings/clients/habits/${habitId}`);
  }
  
  async addHabit(orgId: string, data: Partial<UserHabit>) {
    return this.dbService.pushByRef(`organizations/${orgId}/settings/clients/habits`, data);
  }
  
  async updateHabit(orgId: string, habitId: string,  data: Partial<UserHabit>) {
    return this.dbService.updateRef(`organizations/${orgId}/settings/clients/habits/${habitId}`, data);
  }

  deleteHabit(orgId: string, habitId: string) {
    return this.dbService.removeRef(`organizations/${orgId}/settings/clients/habits/${habitId}`);
  }

  handleData(res): Promise<any> {
    return Promise.resolve(res)
  }

  handleRejection(err): Promise<any> {
    if(err) {
      if(err.error) {
        return Promise.reject(err.error)
      } else {
        return Promise.reject(err);
      }
    } else {
      return Promise.reject({code: "unknown-error"});
    }
  }

  async changeOrganization(orgId: string): Promise<Organization> {
    const newOrg = await this.getOrganizationAsPromise(orgId);
    this.setAppOrganization(newOrg);
    return newOrg;
  }

  disableClient(accountId: string, userId: string) {
    return this.functionsService.callFunction("disableClient", {
      accountId: accountId,
      userId: userId,
    });
  }

  // Account Usage
  async getAccountUsageRecords(orgId: string) {
    const snap = await this.dbService.getSnapshot(`accountUsage/${orgId}/records`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
  }

  async getAccountReferrals(accountId: string) {
    const snap = await this.dbService.getSnapshot(`referrals/${accountId}`);
    if(!snap || !snap.organizations) {
      return null;
    }

    return this.helperService.transformFirebaseObjectToArray(snap.organizations);
  }

  // Product Links
  addProduct(product: Partial<IProductLink>) {
    return this.dbService.pushByRef(`products`, product);
  }

  async addProductPhoto(productId: string, imgData) {
    const downloadURL = await this.storageService.fbPutString(`products/${productId}`, imgData);
    await this.updateProduct(productId, {photo: downloadURL});
    return downloadURL
  }

  async removeProductPhoto(productId: string) {
    return this.storageService.fbRemoveFile(`products/${productId}`);
  }

  updateProduct(productId: string, product: Partial<IProductLink>) {
    return this.dbService.updateRef(`products/${productId}`, product);
  }

  removeProduct(productId: string) {
    return this.dbService.removeRef(`products/${productId}`);
  }

  getProductsByAccount(orgId: string) {
    return this.dbService.getListByRefTest(`products`, orderByChild("organization"), equalTo(orgId));
  }

  async getProductsByAccountPromise(orgId: string): Promise<IProductLink[]> {
    const snap = await this.dbService.getSnapshot(`products`, orderByChild("organization"), equalTo(orgId));
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
  }

  async getProductAsPromise(productId: string): Promise<IProductLink> {
    const snap = await this.dbService.getSnapshot(`products/${productId}`)
    if(snap) {
      return {
        ...snap,
        id: productId
      }
    } else {
      return null
    }
  }
}

